import React from 'react';

import { Alert, Box, Card, Collapse, Divider, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import ReadonlyWorkoutNotesRTE from 'components/workout-program/ReadonlyWorkoutNotesRTE';
import type { WorkoutRetrieve } from 'types';
import type { WorkoutSectionRetrieve } from 'types/generated/models/WorkoutSectionRetrieve';
import { getAlphabetLetterByIndex } from 'utils/functions';

interface AthleteWorkoutSectionDetailProps {
  workout: WorkoutRetrieve;
  isOpened: boolean;
}

interface WorkoutSectionProps {
  section: WorkoutSectionRetrieve;
  index: number;
}

const WorkoutSection = ({ section, index }: WorkoutSectionProps): JSX.Element => {
  const theme = useMantineTheme();
  const sectionLetter = getAlphabetLetterByIndex(index);
  const hasMovements = section.movements && section.movements.length > 0;

  return (
    <div>
      <Group spacing='xs' align='center' mb='sm'>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundColor: theme.colors.blue[6],
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: theme.fontSizes.sm,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {sectionLetter}
        </Box>
        <Text weight={600} size='sm'>
          {section.name}
        </Text>
      </Group>
      <Card p='sm' withBorder radius='md' sx={{ backgroundColor: theme.white }}>
        {section.workoutInstructions?.trim() ? (
          <ReadonlyWorkoutNotesRTE workoutNotes={section.workoutInstructions} mih={0} bordered={false} />
        ) : (
          <Text size='sm' italic color='dimmed'>
            No section description
          </Text>
        )}
        {hasMovements && (
          <>
            <Divider my='sm' />
            <Stack spacing={4}>
              {section.movements
                .slice()
                .sort((a, b) => a.orderIndex - b.orderIndex)
                .map(movement => (
                  <Text key={movement.workoutSectionMovementId} size='sm'>
                    {movement.workoutMovement?.name ?? movement.workoutMovementName}
                  </Text>
                ))}
            </Stack>
          </>
        )}
      </Card>
    </div>
  );
};

const AthleteWorkoutSectionDetail = ({
  workout,
  isOpened,
}: AthleteWorkoutSectionDetailProps): JSX.Element => {
  return (
    <Collapse in={isOpened} transitionTimingFunction='linear'>
      <Stack spacing='lg'>
        {workout.athleteNotes && (
          <Alert icon={<IconInfoCircle size={18} />} title='Athlete Notes' color='blue' variant='light'>
            <ReadonlyWorkoutNotesRTE workoutNotes={workout.athleteNotes} mih={0} bordered={false} />
          </Alert>
        )}

        {workout.sections.length > 0 && (
          <Stack spacing='lg'>
            {workout.sections
              .slice()
              .sort((a, b) => a.orderIndex - b.orderIndex)
              .map((section, index) => (
                <WorkoutSection key={section.workoutSectionId} section={section} index={index} />
              ))}
          </Stack>
        )}
      </Stack>
    </Collapse>
  );
};

export default AthleteWorkoutSectionDetail;
